/**
 * Thin fetch wrapper around the generated `paths` types.
 *
 * Auth is attached centrally: `apiFetch` fills the `Authorization`
 * header from the module-level `TokenSource` (registered by
 * `AuthContext`) for any caller that didn't already set one (#773). The
 * wrapper itself stays React-context-agnostic so it can be imported from
 * places that don't have the context (Zustand stores, etc.).
 *
 * Errors throw `ApiError`; consumers using TanStack Query get retries
 * + caching for free, manual callers get `try/catch`.
 */

import { getAuthHeaderValue } from "../lib/tokenSource";
import type { paths } from "./schema.gen";

export type ApiPaths = paths;

const API_BASE = import.meta.env.VITE_API_URL ?? "";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
    readonly traceId?: string,
  ) {
    super(`API ${status}`);
  }

  /** Best-effort extraction of the user-facing detail from the canonical
   * error envelope (`{ error: { type, title, detail, trace_id } }`).
   * Falls back to the HTTP status for malformed bodies. */
  get detail(): string {
    if (this.body && typeof this.body === "object") {
      const errorBody = (this.body as { error?: unknown }).error;
      if (errorBody && typeof errorBody === "object") {
        const detail = (errorBody as { detail?: unknown }).detail;
        if (typeof detail === "string" && detail) return detail;
        const title = (errorBody as { title?: unknown }).title;
        if (typeof title === "string" && title) return title;
      }
    }
    return `Request failed (${this.status})`;
  }
}

/** True when the page runs as an installed app (home-screen icon), not a browser tab. */
function isInstalledApp(): boolean {
  try {
    if (
      typeof window.matchMedia === "function" &&
      window.matchMedia("(display-mode: standalone)")?.matches
    ) {
      return true;
    }
  } catch {
    // A missing or broken matchMedia reads as a browser tab.
  }
  return (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

/**
 * `app` for the phone app: the `/me` paths, or any path when the page runs as an
 * installed app (its start URL is `/`, which calls the API before it redirects
 * to `/me`). Otherwise `web` (#2833).
 */
export function surfaceForPath(pathname: string): "app" | "web" {
  if (pathname === "/me" || pathname.startsWith("/me/")) return "app";
  return isInstalledApp() ? "app" : "web";
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  // Headers must be merged via the Headers API (case-insensitive .has/.set),
  // not plain-object spread. A caller passing `{"content-type": "..."}`
  // alongside our `{"Content-Type": "..."}` default produces TWO distinct
  // JS keys; fetch's normaliser then appends both into a single wire header
  // valued "application/json, application/json", which FastAPI/Starlette
  // refuses to parse as JSON → 422 RequestValidationError. See #267.
  const headers = new Headers(init?.headers ?? {});
  // #1735 — never force JSON onto a FormData body: the browser must set
  // `multipart/form-data` WITH its boundary itself, and a pre-set
  // Content-Type suppresses that (FastAPI then 422s the upload).
  if (!headers.has("content-type") && !(init?.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  // Central TokenSource injection (#773). ONLY fill the `Authorization`
  // gap — never override a header a caller already set. The central
  // source is now the only auth path: callers no longer pass explicit
  // `authHeaders(accountId)`, so this fills auth for essentially every
  // request. The sole remaining explicit-header caller is AuthContext's
  // own `/api/account/me` dev-shim bootstrap, which `.has("authorization")`
  // here and is left UNCHANGED. `getAuthHeaderValue()` is defensive — it
  // returns `null` (never throws) when there's no session or a mint
  // fails, so a token failure can't break this fetch.
  if (!headers.has("authorization")) {
    const authValue = await getAuthHeaderValue();
    if (authValue) {
      headers.set("Authorization", authValue);
    }
  }
  // #2833 — name the surface so the API can stamp last-active per surface.
  // `app` is the phone app at `/me`; everything else is `web`. A caller's own
  // value wins. An old API ignores the header. Sent only with `Authorization`,
  // so public pages need no extra CORS preflight.
  if (headers.has("authorization") && !headers.has("x-tarii-surface")) {
    headers.set("X-Tarii-Surface", surfaceForPath(window.location.pathname));
  }
  const response = await fetch(`${API_BASE}${path}`, { ...init, headers });
  const body = (await response.json().catch(() => null)) as unknown;
  if (!response.ok) {
    const traceId = response.headers.get("x-request-id") ?? undefined;
    throw new ApiError(response.status, body, traceId);
  }
  return body as T;
}
